import { marketApi, Site } from '@envato/foundation-types'
import * as classnames from 'classnames'
import * as React from 'react'

import { reportProductsImpressions } from '../../../../common/analytics/resultAnalytics'
import { isSearchResultMatchesEqual } from '../../../../common/utils/item'
import { getConfigBySite } from '../../../../config/sites'
import {
  IHomePageBlockComponent,
  ISearchCta
} from '../../../../typings/pageBlocks'
import Button from '../../button'
import Dropdown from '../../dropdown'
import ChevronDownIcon from '../../icons/chevronDown'
import SearchLink from '../../../containers/searchLinkContainer'
import ItemCard from '../../itemCard'
import ItemCardsLayout from '../../itemCardsLayout'
import { IItem } from '../../../../typings/searchResults'
import { getWindow } from '../../../../common/utils/window'
import { reportBlockClick } from '../../../../common/analytics/homePageAnalytics'
import * as styles from './newestItemsBlock.module.css'

interface IProps extends IHomePageBlockComponent {
  data: IItem[]
  title: string
  subtitle?: string
  categories?: marketApi.CategoriesResult
  currentSite: Site
  cta: ISearchCta
  itemsPendingCategory: string | undefined
  loadingCachedCategory: boolean
  fetchNewestItemsByCategory: (category: string) => any
  itemsCategory: string
}

interface ICategoryOption {
  label: string
  value: string
}

export const reportNewestItemsClick = (
  eventLabel: string,
  eventValue?: number
) => {
  reportBlockClick('Newest Items', {
    dimension19: 'body',
    eventLabel,
    eventValue
  })
}

class NewestItemsBlock extends React.Component<IProps> {
  public componentDidUpdate(prevProps: IProps) {
    const { data, currentSite } = this.props

    if (!isSearchResultMatchesEqual(prevProps.data, data)) {
      reportProductsImpressions({
        currentSite,
        itemRankOffset: 1,
        items: data,
        page: 'Homepage',
        section: 'Newest Items'
      })
    }
  }

  public render() {
    const {
      data,
      title,
      subtitle,
      categories,
      currentSite,
      cta,
      itemsPendingCategory,
      loadingCachedCategory
    } = this.props

    const hideCategories = getConfigBySite(currentSite, 'hide_categories')

    const categoriesOptions = categories
      ? [
          {
            name: 'All categories',
            path: 'all'
          },
          ...categories
        ].map(category => {
          return {
            label: category.name,
            value: category.path
          }
        })
      : []

    // This is mainly for Photodune
    const shouldShowCategories = !hideCategories && categories
    const isLoading = itemsPendingCategory && !loadingCachedCategory

    return (
      <section className={styles.root} data-test-selector="newestItemsBlock">
        <div className={styles.wrapper}>
          <header className={styles.header}>
            <h2 className={styles.title}>{title}</h2>
            {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
          </header>
          {shouldShowCategories && this.renderCategoriesList(categoriesOptions)}
          {shouldShowCategories &&
            this.renderCategoriesDropdown(categoriesOptions)}
          <div className={styles.itemsGridWrapper}>
            <ItemCardsLayout viewType="grid" className={styles.itemsGrid}>
              {data.map((item, index) => (
                <ItemCardsLayout.CardArea key={item.id}>
                  <ItemCard
                    listName="Homepage: Newest Items"
                    item={item}
                    key={item.id}
                    index={index}
                    viewType="grid"
                  />
                </ItemCardsLayout.CardArea>
              ))}
            </ItemCardsLayout>
            <div className={isLoading ? styles.loadingCover : ''} />
          </div>
          <footer className={styles.footer}>
            <Button
              large
              type={Button.Type.Primary}
              fixedWidth
              locationParams={cta.urlSearchParams}
              renderAs={SearchLink}
              onClick={() => {
                reportNewestItemsClick(cta.title)
                getWindow().scrollTo(0, 0)
              }}
            >
              {cta.title}
            </Button>
          </footer>
        </div>
      </section>
    )
  }

  private renderCategoriesDropdown(options: ICategoryOption[]) {
    const {
      itemsPendingCategory,
      fetchNewestItemsByCategory,
      itemsCategory
    } = this.props

    return (
      <div className={styles.dropdownArea}>
        <Dropdown
          aria-label="Category"
          className={styles.dropdown}
          options={options}
          onChange={event => {
            const selectedItemIndex = event.target.selectedIndex
            reportNewestItemsClick(
              event.target.options[selectedItemIndex].text,
              selectedItemIndex
            )
            fetchNewestItemsByCategory(event.target.value)
          }}
          value={itemsPendingCategory || itemsCategory}
        />
        <ChevronDownIcon
          width="22"
          height="14"
          className={styles.chevronIcon}
        />
      </div>
    )
  }

  private renderCategoriesList(options: ICategoryOption[]) {
    const {
      itemsPendingCategory,
      fetchNewestItemsByCategory,
      itemsCategory
    } = this.props

    return (
      <div className={styles.categoriesNav}>
        {options.map((category, index) => {
          const buttonSelected =
            itemsPendingCategory === category.value ||
            (!itemsPendingCategory && itemsCategory === category.value)
          const categoryClassName = classnames(styles.categoryLink, {
            [styles.activeCategoryLink]: buttonSelected
          })

          return (
            <button
              className={categoryClassName}
              key={category.value}
              onClick={() => {
                reportNewestItemsClick(category.label, index)
                fetchNewestItemsByCategory(category.value)
              }}
            >
              {category.label}
            </button>
          )
        })}
      </div>
    )
  }
}

export default NewestItemsBlock
